'use client';

import Button from '@/components/base/button/button';
import Form from '@/components/base/form/form-control/form';
import FormControl from '@/components/base/form/form-control/form-control';
import Input from '@/components/base/form/input/input';
import PasswordInput from '@/components/base/form/input/password-input';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { PopularDomainsPrompting } from '@/components/base/popular-domains-prompting/popular-domains-prompting';
import { addAuthTokenParams } from '@/features/login/utils/add-auth-token-params';
import { getCorrectPathAfterLogin } from '@/features/login/utils/get-correct-path-after-login';
import { useSetCookie } from '@/hooks/use-cookies';
import { useCustomer, useLoginAndStoreData } from '@/hooks/use-customer';
import { useEmailCheck } from '@/hooks/use-email-check';
import { useLoginCallbacks } from '@/hooks/use-login-callbacks';
import { useRouter } from '@/navigation';
import { ACCOUNT_ROUTES, LOGIN_ROUTES } from '@/routes';
import { useLoginSchema } from '@/schemas/zod/schemas';
import { Cookie, CustomerLoginType, SessionStorage } from '@/types/storage';
import { COOKIES_EXPIRATION_DATE } from '@/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from '@phosphor-icons/react/dist/ssr';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useTransition } from 'react';
import { useForm } from 'react-hook-form';

export type LoginInputs = {
  email: string;
  password?: string;
};

export enum CallbackActions {
  ADD_TO_FAVORITE = 'addToFavorite',
  SAVE_UPSELL_BASKET_CONSENTS = 'saveUpsellBasketMarketingConsents',
}

const LogInWithEmail = () => {
  const t = useTranslations('login');
  const tc = useTranslations('common');
  const [isRegisterTransitionPending, startRegisterTransition] =
    useTransition();

  const { email } = useCustomer();

  const setCookie = useSetCookie();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    watch,
    setValue,
    reset,
    formState: { errors, isLoading: isLoadingForm, isSubmitting },
  } = useForm<LoginInputs>({
    resolver: zodResolver(useLoginSchema()),
    defaultValues: {
      email: '',
    },
  });

  useEffect(() => {
    if (email && email !== 'undefined') {
      reset({
        email,
      });
    }
  }, [email, reset]);

  const { checkEmail, isPending, isEmailExists } = useEmailCheck();

  const { loginUserAndStoreData, isTransitionPending } = useLoginAndStoreData(
    ACCOUNT_ROUTES.DASHBOARD,
  );

  const handleCheckUser = async ({ email }: LoginInputs) => {
    setCookie(Cookie.CUSTOMER_EMAIL, email, {
      expires: COOKIES_EXPIRATION_DATE,
    });
    const { isEmailExists } = await checkEmail(email);
    if (!isEmailExists) {
      startRegisterTransition(() => {
        router.push(LOGIN_ROUTES.REGISTER);
      });
    }
  };

  const { loginCallback } = useLoginCallbacks();

  const isLoadingState =
    isPending ||
    isTransitionPending ||
    isSubmitting ||
    isLoadingForm ||
    isRegisterTransitionPending;

  const handleLoginUser = async (data: LoginInputs) => {
    const onSuccessLogin =
      sessionStorage.getItem(SessionStorage.ON_SUCCESSFULLY_LOGIN) ??
      ACCOUNT_ROUTES.DASHBOARD;

    setCookie(Cookie.CUSTOMER_LOGIN_TYPE, CustomerLoginType.LOGGED_IN);

    const handleOnSuccess = (token: string) => {
      sessionStorage.removeItem(SessionStorage.ON_SUCCESSFULLY_LOGIN);

      const path = getCorrectPathAfterLogin(onSuccessLogin);
      const pathWithToken = addAuthTokenParams(path, token);

      router.push(pathWithToken);
      loginCallback();
    };

    const { success } = await loginUserAndStoreData(data, handleOnSuccess);
    if (!success) {
      setError('password', {
        type: 'manual',
        message: tc('error.invalid_credentials'),
      });
    }
  };

  return (
    <div className="container flex flex-col items-center">
      <Form
        onSubmit={handleSubmit(
          !isEmailExists ? handleCheckUser : handleLoginUser,
        )}
        name="email"
        className="flex w-full flex-col items-center"
      >
        <div className="flex m:w-max-form-width w-full max-w-max-form-width flex-col items-center">
          <div
            className={clsx(
              'flex w-full flex-col items-start',
              isEmailExists ? 'max-s:pt-xxl' : 'max-s:pt-[108px]',
            )}
          >
            <h1 className="self-stretch pt-m s:pt-xl pb-s text-left s:text-center text-heading-l text-strong">
              {isEmailExists
                ? t('email_login.title')
                : t('email_login.email.label')}
            </h1>
          </div>
          <div className="flex w-full flex-col items-start">
            {isEmailExists ? (
              <span className="mb-xs font-semibold text-body">{email}</span>
            ) : (
              <FormControl
                className="mb-xs w-full"
                errorMessage={errors.email?.message}
              >
                <Input
                  className="w-full"
                  type="email"
                  testId="email"
                  error={!!errors.email}
                  prepend={
                    <PopularDomainsPrompting
                      value={watch('email')}
                      onChange={(value) =>
                        setValue('email', value, { shouldValidate: true })
                      }
                    />
                  }
                  {...register('email')}
                />
              </FormControl>
            )}

            {isEmailExists && (
              <FormControl
                label={tc('password.label')}
                className="w-full"
                errorMessage={errors.password?.message}
              >
                <PasswordInput
                  className="w-full"
                  type="password"
                  autoFocus
                  testId="password"
                  error={!!errors.password}
                  {...register('password')}
                />
              </FormControl>
            )}
          </div>
        </div>
        <div className="flex m:w-max-button-width w-full flex-col items-center gap-xs pt-s">
          <Button
            variant="primary"
            size="large"
            className="w-full xs:max-w-max-button-width"
            type="submit"
            testId={
              isEmailExists ? 'login_and_continue_button' : 'continue_button'
            }
            isLoading={isLoadingState}
          >
            {isEmailExists ? (
              t('email_login.btn_continue')
            ) : (
              <div className="flex items-center justify-center gap-xxs no-flex-gap:space-x-xxs">
                <span>{tc('continue')}</span>
                <PhosphorIcon Icon={ArrowRight} className="icon-m" />
              </div>
            )}
          </Button>
          <Button
            variant="tertiary-no-frame"
            href={LOGIN_ROUTES.RESET_PASSWORD}
            size="large"
            isLink
            className="flex w-full items-center justify-center underline"
          >
            {t('email_login.forgot_password')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default LogInWithEmail;
